import {
  Bold,
  Italic,
  Strikethrough,
  Underline,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  ImagePlus,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TiptapEditor } from '@/domains/types/editor'
import { classNames } from '@/ui/utils/main'

interface RichTextToolbarProps {
  editor: TiptapEditor | null
  onInsertImage?: () => void
  isUploadingImage?: boolean
  allowImages?: boolean
}

interface ToolBtnProps {
  active: boolean
  disabled?: boolean
  onMouseDown: React.MouseEventHandler<HTMLButtonElement>
  title: string
  children: React.ReactNode
}

function ToolBtn({ active, disabled, onMouseDown, title, children }: ToolBtnProps) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={onMouseDown}
      className={classNames(
        'p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        active
          ? 'bg-primary/20 text-primary'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  )
}

/**
 * Formatting toolbar for `RichTextEditor`. Every button uses
 * `onMouseDown` + `preventDefault()` (not `onClick`) so the editor's text
 * selection survives the click — losing it would make toggling a mark on a
 * selection impossible.
 */
export function RichTextToolbar({
  editor,
  onInsertImage,
  isUploadingImage,
  allowImages,
}: RichTextToolbarProps) {
  const { t } = useTranslation()
  if (!editor) return null

  const separator = <div className="w-px h-4 bg-border mx-0.5" />

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-border bg-card/50">
      <ToolBtn
        active={editor.isActive('bold')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleBold().run()
        }}
        title={t('editor.bold')}
      >
        <Bold className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn
        active={editor.isActive('italic')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleItalic().run()
        }}
        title={t('editor.italic')}
      >
        <Italic className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn
        active={editor.isActive('strike')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleStrike().run()
        }}
        title={t('editor.strike')}
      >
        <Strikethrough className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn
        active={editor.isActive('underline')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleUnderline().run()
        }}
        title={t('editor.underline')}
      >
        <Underline className="w-3.5 h-3.5" />
      </ToolBtn>

      {separator}

      <ToolBtn
        active={editor.isActive('heading', { level: 2 })}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }}
        title={t('editor.heading2')}
      >
        <Heading2 className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn
        active={editor.isActive('heading', { level: 3 })}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }}
        title={t('editor.heading3')}
      >
        <Heading3 className="w-3.5 h-3.5" />
      </ToolBtn>

      {separator}

      <ToolBtn
        active={editor.isActive('bulletList')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleBulletList().run()
        }}
        title={t('editor.bullet_list')}
      >
        <List className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn
        active={editor.isActive('orderedList')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleOrderedList().run()
        }}
        title={t('editor.ordered_list')}
      >
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolBtn>

      {separator}

      <ToolBtn
        active={editor.isActive('blockquote')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleBlockquote().run()
        }}
        title={t('editor.quote')}
      >
        <Quote className="w-3.5 h-3.5" />
      </ToolBtn>
      <ToolBtn
        active={editor.isActive('code')}
        onMouseDown={(event) => {
          event.preventDefault()
          editor.chain().focus().toggleCode().run()
        }}
        title={t('editor.code')}
      >
        <Code className="w-3.5 h-3.5" />
      </ToolBtn>

      {allowImages && onInsertImage && (
        <>
          {separator}
          <ToolBtn
            active={false}
            disabled={isUploadingImage}
            onMouseDown={(event) => {
              event.preventDefault()
              onInsertImage()
            }}
            title={
              isUploadingImage
                ? t('editor.uploading')
                : `${t('editor.insert_image')} — ${t('editor.image_resize_hint')}`
            }
          >
            {isUploadingImage ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <ImagePlus className="w-3.5 h-3.5" />
            )}
          </ToolBtn>
        </>
      )}
    </div>
  )
}
